// src/lib/accueil-cartes.js — LES TROIS AUTRES CARTES DE L'ACCUEIL.
//
// ⚠️ MODULE PUR — zéro I/O, zéro React. Même discipline que
// `accueil-attend.js` : le calcul se teste seul, l'écran ne fait que le rendre.
//
// ⚠️ CE QUE LA MAQUETTE PORTE ET QUE LA BASE NE TIENT PAS — donc ce que cet
// écran NE MONTRE PAS (§8.2 du cadrage, « dis-moi lesquelles n'existent pas
// avant de les inventer ») :
//   · le jugement d'urgence de l'opérateur (« l'opérateur l'a jugé urgent ») ;
//   · la réponse préparée (le badge « prête »).
// Aucune colonne d'`inbox_messages` ne les porte. Les afficher aurait été
// exactement le défaut qu'on chasse : une affirmation posée sur du vide. Ils
// reviendront avec l'opérateur, qui est le seul à pouvoir les produire.

import { filsQuiAttendent } from "./fils-attente-ordre";
import { jourCourt } from "./accueil-attend";

/** « 9h12 » / « 17h » — le format de l'application, jamais « 09:12 ». */
export function heureCourteDe(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const [h, m] = new Intl.DateTimeFormat("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Europe/Paris",
  })
    .format(date)
    .split(":");
  return m === "00" ? `${Number(h)}h` : `${Number(h)}h${m}`;
}

/** Date civile Paris d'un instant — pour comparer « est-ce aujourd'hui ». */
export function jourParis(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Paris" }).format(
    date,
  );
}

/** « 9h12 » aujourd'hui · « hier » · « le 12 août » au-delà. */
export function quandCourt(iso, todayIso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  const j = jourParis(d);
  if (j === todayIso) return heureCourteDe(d);
  const veille = new Date(`${todayIso}T12:00:00Z`);
  veille.setUTCDate(veille.getUTCDate() - 1);
  if (j === jourParis(veille)) return "hier";
  return `le ${jourCourt(jourParis(d))}`;
}

/** « MR » — les initiales d'un nom. Une adresse mail vaut un nom faute de mieux. */
export function initiales(nom, email) {
  const n = typeof nom === "string" ? nom.trim() : "";
  if (n) {
    return n
      .split(/\s+/)
      .slice(0, 2)
      .map((x) => x[0])
      .join("")
      .toUpperCase();
  }
  const e = typeof email === "string" ? email.trim() : "";
  return e ? e.slice(0, 2).toUpperCase() : "—";
}

/**
 * LES FILS SANS RÉPONSE.
 *
 * ⚠️ « SANS RÉPONSE » N'EST PAS « NON LU ». Un fil attend quand son DERNIER
 * message est ENTRANT : on a reçu, on n'a pas répondu. Marquer lu ne répond
 * pas ; répondre sans marquer lu répond quand même. Le compteur de la barre
 * (non lus) répond à une AUTRE question — les deux peuvent différer sans que
 * ce soit une divergence.
 *
 * LOT 1 OPÉRATEUR (D1) — `filsAvecProposition` (OPTIONNEL, Set de thread_id) :
 * les fils dont une réponse est PRÊTE portent le tag « prête », qui SURVIT sur
 * la carte. La ligne n'envoie rien — le fil s'ouvre en MODE RENVOI (« Elle
 * vous attend dans vos décisions »). Défaut absent = sortie identique.
 *
 * @param {Array} messages triés du plus RÉCENT au plus ancien (l'ordre de la requête).
 */
export function filsEnAttente(
  messages,
  { todayIso, nomLogement, filsAvecProposition } = {},
) {
  // ⚠️ LE RANGEMENT EST PARTAGÉ AVEC L'OPÉRATEUR (2026-09-12). Cette boucle
  // supposait que l'appelant trie du plus récent au plus ancien (« le premier
  // vu EST le plus récent ») — c'était vrai ici, et faux côté opérateur, qui
  // triait à l'envers. Deux tris opposés derrière le même plafond gardaient des
  // fils DISJOINTS : Dimitri voyait Chloé Raynal, l'opérateur ne la connaissait
  // pas. `filsQuiAttendent` ne suppose plus rien de l'ordre d'entrée.
  const out = [];
  for (const m of filsQuiAttendent(messages)) {
    const fil = m.thread_id || m.id;
    const nom =
      (typeof m.sender_name === "string" && m.sender_name.trim()) ||
      (typeof m.sender_email === "string" && m.sender_email.trim()) ||
      "Inconnu";
    const prete = !!(filsAvecProposition && filsAvecProposition.has(fil));
    out.push({
      cle: `fil:${fil}`,
      initiales: initiales(m.sender_name, m.sender_email),
      titre: nom,
      sousTitre: [
        nomLogement ? nomLogement(m.logement_id) : m.logement_id,
        quandCourt(m.created_at, todayIso),
      ]
        .filter(Boolean)
        .join(" · "),
      ...(prete ? { tag: "prête" } : {}),
      cible: { ecran: "messages", threadId: fil },
    });
  }
  return out;
}

/**
 * MES TÂCHES — DEUX LIGNES AU MAXIMUM, jamais la liste entière.
 *
 * ⚠️ « AUJOURD'HUI » INCLUT LE RETARD, ET IL FAUT LE DIRE (Dimitri). Le calcul
 * de l'écran des tâches range les échues dans « aujourd'hui » — c'est juste,
 * mais le MOT ment : trois tâches en retard depuis le 31 juillet ne sont pas
 * « pour aujourd'hui ». On les sépare donc ici, et la ligne du retard porte sa
 * date d'origine.
 */
export function resumeTaches(taches, { todayIso, nomLogement } = {}) {
  const ouvertes = (taches || []).filter(
    (t) => t && t.status !== "done" && !t.archived_at,
  );
  const avecDate = ouvertes
    .map((t) => ({ t, jour: t.due_at ? jourParis(new Date(t.due_at)) : null }))
    .filter((x) => x.jour);
  const enRetard = avecDate
    .filter((x) => x.jour < todayIso)
    .sort((a, b) => a.jour.localeCompare(b.jour));
  const pourAujourdhui = avecDate.filter((x) => x.jour === todayIso);

  const lignes = [];
  if (enRetard.length) {
    const plusAncienne = enRetard[0].jour;
    lignes.push({
      cle: "taches:retard",
      ton: "terra",
      pastille: "!",
      titre: `${enRetard.length} en retard`,
      sousTitre: `depuis le ${jourCourt(plusAncienne)}`,
      cible: { ecran: "mes-taches" },
    });
  }
  if (pourAujourdhui.length) {
    const une = pourAujourdhui[0].t;
    lignes.push({
      cle: "taches:aujourdhui",
      ton: "neutre",
      pastille: "☑",
      titre: `${pourAujourdhui.length} pour aujourd'hui`,
      sousTitre: [
        (typeof une.title === "string" && une.title.trim()) || null,
        une.logement_id && nomLogement ? nomLogement(une.logement_id) : null,
      ]
        .filter(Boolean)
        .join(" · "),
      cible: { ecran: "mes-taches" },
    });
  }
  // ⚠️ Le compteur de la carte compte CE QUI ATTEND UNE DATE PASSÉE OU DU JOUR
  // — pas le stock entier de tâches ouvertes. Un compteur qui dit 47 devant
  // deux lignes ne veut rien dire.
  return {
    lignes,
    total: enRetard.length + pourAujourdhui.length,
    ouvertes: ouvertes.length,
  };
}

// ⚠️ UNE SEULE ÉCRITURE DE DATE POUR TOUT L'ACCUEIL — `jourCourt` vit dans
// accueil-attend.js et est importé en tête. En avoir deux copies, c'était les
// voir diverger au premier ajustement — et c'est arrivé : l'une écrivait
// « 1 août » là où l'autre écrivait « 1er août ».

/**
 * PROSPECTION — UNE SEULE LIGNE : les pistes à regarder, sur le parc collecté.
 *
 * LOT 4 OPÉRATEUR B (2026-09-06) : l'ancien « N contactables sur M » comptait
 * un prédicat MORT (email/tel, hérité du cleanup retiré) et « l'envoi est
 * coupé » parlait d'un canal supprimé (les vagues d'emails n'existent plus —
 * la file in-app Logements › À démarcher les remplace). « À regarder » =
 * pas_contacte non masquée — LE MÊME prédicat que le compteur de la bascule
 * Logements (serveur : countARegarder, lib/prospects-store.js).
 */
export function ligneProspection({ total, aRegarder }) {
  const t = Number(total) || 0;
  const n = Number(aRegarder) || 0;
  return {
    cle: "prospection",
    pastille: "◈",
    titre: `${n} piste${n > 1 ? "s" : ""} à regarder`,
    sousTitre: `sur ${t.toLocaleString("fr-FR")}`,
    // La destination RÉELLE : l'onglet Logements, dont la bascule de tête
    // ouvre « À démarcher ». (L'ancien `ecran: 'prospection'` désignait un
    // onglet supprimé — un lien mort qui n'aurait rien ouvert.)
    cible: { ecran: "logements" },
  };
}
